import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, Optional } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { environment } from "../../../environments/environment";
import {
  ActivityHistoryPage, DecisionActionRequest, DecisionActionResult, DecisionDetail, DecisionPage,
  DecisionState, DecisionSummary, DetectedDatesSummary, DocumentPulse, HomeCalendarGranularity,
  HomeCalendarViewMode, HomeMetric, NextBestDecision, SystemActivity
} from "../../models/home";
import { KnowledgeAttentionResponse } from "../../models/home/knowledge-attention/knowledge-attention.model";
import { RecentChangesResponse } from "../../models/home/recent-changes/recent-changes.model";
import { UxError } from "../../models/ux-error.model";
import { FrontendLoggerService } from "../../services/frontend-logger.service";
import { HomeDataService } from "../../services/home-ia/home-data.service";
import { HomeDecisionActionUxService } from "../../services/home-ia/home-decision-action-ux.service";
import { HomeKnowledgeAttentionService } from "../../services/home-ia/home-knowledge-attention.service";
import { HomeRecentChangesService } from "../../services/home-ia/home-recent-changes.service";
import { ProblemDetailsHandler } from "../../services/home-ia/problem-details-handler.service";
import { HomeStreamEvent, HomeStreamService } from "../../services/home-ia/home-stream.service";
import { LanguageService } from "../../services/language/language.service";
import { HomeDecisionActionCoordinator } from "./home-decision-action.coordinator";
import { HomeDashboardStore } from "./home.component.dashboard-store";
import { HomeDashboardInteractionController } from "./home-dashboard-interaction.controller";
import { HomeDashboardLifecycle } from "./home-dashboard-lifecycle";
import { HomeDashboardRefreshController } from "./home-dashboard-refresh.controller";
import { HomeLoadStateFactory } from "./home.component.load-state";
import { HomeStreamEventCoordinator } from "./home-stream-event.coordinator";
import { DecisionStateFilter, HomeAutoRefreshTarget, LoadState, PendingActionEchoContext } from "./home.component.state";
export type { HomeAutoRefreshTarget, LoadState, PendingActionEchoContext } from "./home.component.state";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly store: HomeDashboardStore;
  private readonly refresh: HomeDashboardRefreshController;
  private readonly actions: HomeDecisionActionCoordinator;
  private readonly streamEvents: HomeStreamEventCoordinator;
  private readonly lifecycle: HomeDashboardLifecycle;
  private readonly interactions: HomeDashboardInteractionController;
  private readonly loadStates: HomeLoadStateFactory;

  constructor(
    private readonly homeDataService: HomeDataService,
    private readonly problemDetailsHandler: ProblemDetailsHandler,
    public readonly lang: LanguageService,
    private readonly homeStreamService: HomeStreamService,
    private readonly actionUx: HomeDecisionActionUxService,
    private readonly frontendLogger: FrontendLoggerService,
    @Optional() private readonly route: ActivatedRoute | null,
    knowledgeAttentionService: HomeKnowledgeAttentionService,
    recentChangesService: HomeRecentChangesService
  ) {
    this.store = new HomeDashboardStore(
      homeDataService, knowledgeAttentionService, recentChangesService, problemDetailsHandler
    );
    this.refresh = new HomeDashboardRefreshController(this.store);
    this.actions = new HomeDecisionActionCoordinator(
      this.store, homeDataService, problemDetailsHandler, lang, actionUx, frontendLogger
    );
    this.streamEvents = new HomeStreamEventCoordinator(this.store, this.actions.echo, this.refresh);
    this.actions.attachStreamEvents(this.streamEvents);
    this.lifecycle = new HomeDashboardLifecycle(this.store, homeStreamService, this.streamEvents);
    this.interactions = new HomeDashboardInteractionController(this.store, this.refresh, this.actions);
    this.loadStates = new HomeLoadStateFactory(problemDetailsHandler);
  }

  get pulseState$(): Observable<LoadState<DocumentPulse>> { return this.store.pulseState$; }
  get overviewState$(): Observable<LoadState<DecisionPage>> { return this.store.overviewState$; }
  get nextBestDecisionState$(): Observable<LoadState<NextBestDecision | null>> { return this.store.nextBestDecisionState$; }
  get decisionListState$(): Observable<LoadState<DecisionPage>> { return this.store.decisionListState$; }
  get calendarDecisionState$(): Observable<LoadState<DecisionPage>> { return this.store.calendarDecisionState$; }
  get decisionDetailState$(): Observable<LoadState<DecisionDetail>> { return this.store.decisionDetailState$; }
  get datesSummaryState$(): Observable<LoadState<DetectedDatesSummary>> { return this.store.datesSummaryState$; }
  get lastActivityState$(): Observable<LoadState<SystemActivity>> { return this.store.lastActivityState$; }
  get activityHistoryState$(): Observable<LoadState<ActivityHistoryPage>> { return this.store.activityHistoryState$; }
  get metricsState$(): Observable<LoadState<readonly HomeMetric[]>> { return this.store.metricsState$; }
  get knowledgeAttentionState$(): Observable<LoadState<KnowledgeAttentionResponse>> { return this.store.knowledgeAttentionState$; }
  get recentChangesState$(): Observable<LoadState<RecentChangesResponse>> { return this.store.recentChangesState$; }
  readonly connectionState$ = this.homeStreamService.connectionState$;
  /** P1-6: gates the two-zone attention/follow-up Home layout (scaffolding flag). */
  readonly attentionLayoutEnabled = environment.homeAttentionLayout === true;
  get selectedDecisionId$() { return this.store.selectedDecisionId$; }
  get activeStateFilter$() { return this.store.activeStateFilter$; }
  get viewMode$() { return this.store.viewMode$; }
  get calendarGranularity$() { return this.store.calendarGranularity$; }
  get calendarAnchorDate$() { return this.store.calendarAnchorDate$; }
  get actionLoading$() { return this.store.actionLoading$; }
  get actionMessage$() { return this.store.actionMessage$; }
  get actionError$() { return this.store.actionError$; }

  private get actionLoadingSubject() { return this.store.actionLoadingSubject; }
  private get actionMessageSubject() { return this.store.actionMessageSubject; }
  private get actionErrorSubject() { return this.store.actionErrorSubject; }
  private get selectedDecisionIdSubject() { return this.store.selectedDecisionIdSubject; }
  private get pendingAutoRefreshTargets() { return this.streamEvents.pendingTargets; }
  private get autoRefreshTimer() { return this.streamEvents.timer; }
  private set autoRefreshTimer(value) { this.streamEvents.timer = value; }
  private get pendingActionEchoContext(): PendingActionEchoContext | null { return this.actions.echo.context; }
  private set pendingActionEchoContext(value: PendingActionEchoContext | null) { this.actions.echo.context = value; }
  private get latestOverviewState() { return this.store.latestOverviewState; }
  private set latestOverviewState(value) { this.store.latestOverviewState = value; }
  private get latestNextBestDecisionState() { return this.store.latestNextBestDecisionState; }
  private set latestNextBestDecisionState(value) { this.store.latestNextBestDecisionState = value; }
  private get latestDecisionListState() { return this.store.latestDecisionListState; }
  private set latestDecisionListState(value) { this.store.latestDecisionListState = value; }
  private get latestCalendarDecisionState() { return this.store.latestCalendarDecisionState; }
  private set latestCalendarDecisionState(value) { this.store.latestCalendarDecisionState = value; }
  private get latestDecisionDetailState() { return this.store.latestDecisionDetailState; }
  private set latestDecisionDetailState(value) { this.store.latestDecisionDetailState = value; }

  private fragmentSub?: Subscription;

  ngOnInit(): void {
    this.lifecycle.initialize((event) => this.handleStreamEvent(event));
    this.fragmentSub = this.route?.fragment.subscribe((fragment) => this.scrollToSection(fragment));
  }
  ngOnDestroy(): void {
    this.fragmentSub?.unsubscribe();
    this.lifecycle.destroy();
  }

  /** Deep-link por fragment: desplaza a la sección con [data-section-id]. Fragment desconocido = no-op. */
  private scrollToSection(fragment: string | null): void {
    if (!fragment) return;
    document
      .querySelector(`[data-section-id="${fragment}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  refreshPulse(): void { this.refresh.refreshPulse(); }
  refreshOverview(): void { this.refresh.refreshOverview(); }
  refreshNextBestDecision(): void { this.refresh.refreshNextBestDecision(); }
  refreshDecisionList(): void { this.refresh.refreshDecisionList(); }
  refreshCalendarDecisionState(): void { this.refresh.refreshCalendarDecisionState(); }
  refreshDecisionDetail(): void { this.refresh.refreshDecisionDetail(); }
  refreshDatesSummary(): void { this.refresh.refreshDatesSummary(); }
  refreshActivity(): void { this.refresh.refreshActivity(); }
  refreshMetrics(): void { this.refresh.refreshMetrics(); }
  refreshKnowledgeAttention(): void { this.refresh.refreshKnowledgeAttention(); }
  refreshRecentChanges(): void { this.refresh.refreshRecentChanges(); }
  onStateFilterChange(state: DecisionStateFilter): void { this.interactions.changeStateFilter(state); }
  onSelectDecision(id: string): void { this.interactions.selectDecision(id); }
  onCloseDecisionDetail(): void { this.interactions.closeDecisionDetail(); }
  onActivityHistoryPageChange(page: number): void { this.interactions.changeActivityHistoryPage(page); }
  onDatesRoleFilterChange(_: string | null): void {}
  onHomeViewModeChange(mode: HomeCalendarViewMode): void { this.interactions.changeViewMode(mode); }
  onCalendarGranularityChange(value: HomeCalendarGranularity): void { this.interactions.changeCalendarGranularity(value); }
  onCalendarPeriodShift(direction: number): void { this.interactions.shiftCalendarPeriod(direction); }
  onJumpToCalendarToday(): void { this.interactions.jumpToToday(); }
  onCalendarAnchorDateChange(value: string): void { this.interactions.changeCalendarAnchorDate(value); }
  onExecuteDecisionAction(event: { decisionId: string; actionKey: string; request?: DecisionActionRequest }): void { this.interactions.executeDecisionAction(event); }
  getDecisionSummary(state: LoadState<DecisionPage> | null | undefined, next: LoadState<NextBestDecision | null> | null | undefined): DecisionSummary | null {
    return this.interactions.getDecisionSummary(state, next);
  }
  toDecisionFilters(state: DecisionStateFilter): { state?: string } | undefined { return this.interactions.toDecisionFilters(state); }

  private createLoadState<T>(refresh$: Observable<void>, load: () => Observable<T>, empty?: (data: T) => boolean) {
    return this.loadStates.create(refresh$, load, empty);
  }
  private createPatchedLoadState<T>(load$: Observable<LoadState<T>>, patch$: Observable<LoadState<T>>, onState: (state: LoadState<T>) => void) {
    return this.loadStates.createPatched(load$, patch$, onState);
  }
  private handleStreamEvent(event: HomeStreamEvent): void { this.streamEvents.handle(event); }
  private shouldRefreshSelectedDecision(event: HomeStreamEvent): boolean { return this.streamEvents.shouldRefreshSelectedDecision(event); }
  private getStreamDecisionId(event: HomeStreamEvent): string | null { return this.streamEvents.getDecisionId(event); }
  private shouldSuppressStreamEvent(event: HomeStreamEvent): boolean { return this.actions.echo.shouldSuppress(event); }
  private matchesDecisionPayload(payload: unknown, id: string): boolean { return this.actions.echo.matchesDecisionPayload(payload, id); }
  private matchesActivityEcho(payload: unknown, context: PendingActionEchoContext): boolean { return this.actions.echo.matchesActivityEcho(payload, context); }
  private asRecord(value: unknown) { return this.actions.echo.asRecord(value); }
  private readString(record: Record<string, unknown> | null, key: string) { return this.actions.echo.readString(record, key); }
  private queueAutoRefreshTargets(...targets: HomeAutoRefreshTarget[]): void { this.streamEvents.queue(...targets); }
  private queueSseRefresh(...targets: HomeAutoRefreshTarget[]): void { this.streamEvents.queueSseRefresh(...targets); }
  private queuePulseAndMetricsRefresh(): void { this.streamEvents.queue("pulse", "metrics"); }
  private flushAutoRefreshTargets(targets: readonly HomeAutoRefreshTarget[]): void { this.streamEvents.flush(targets); }
  private buildDecisionActionRequest(event: { actionKey: string; request?: DecisionActionRequest }) { return this.actions.requests.build(event); }
  private resolvePostponeDays(request: DecisionActionRequest): number { return this.actions.requests.resolvePostponeDays(request); }
  private handleDecisionActionResult(id: string, request: DecisionActionRequest, result: DecisionActionResult): void { this.actions.handleResult(id, request, result); }
  private applyLocalDecisionPatch(id: string, result: DecisionActionResult): void { this.actions.patcher.apply(id, result); }
  private patchOverviewState(id: string, result: DecisionActionResult): void { this.actions.patcher.patchOverviewState(id, result); }
  private patchDecisionListState(id: string, result: DecisionActionResult): void { this.actions.patcher.patchDecisionListState(id, result); }
  private patchCalendarDecisionState(id: string, result: DecisionActionResult): void { this.actions.patcher.patchCalendarDecisionState(id, result); }
  private patchNextBestDecisionState(id: string, result: DecisionActionResult): void { this.actions.patcher.patchNextBestDecisionState(id, result); }
  private patchDecisionDetailState(id: string, result: DecisionActionResult): void { this.actions.patcher.patchDecisionDetailState(id, result); }
  private patchDecisionPage(page: DecisionPage, id: string, result: DecisionActionResult) { return this.actions.patcher.patchDecisionPage(page, id, result); }
  private patchListItem(item: DecisionPage["items"][number], result: DecisionActionResult) { return this.actions.patcher.patchListItem(item, result); }
  private patchDetailItem(item: NonNullable<DecisionDetail["decision"]>, result: DecisionActionResult) { return this.actions.patcher.patchDetailItem(item, result); }
  private resolveActionRefreshTargets(id: string, result: DecisionActionResult) { return this.actions.planner.resolve(id, result); }
  private hasRefreshHint(hints: ReadonlySet<string>, ...values: string[]) { return this.actions.planner.hasHint(hints, ...values); }
  private mapRefreshHintsToTargets(hints: readonly string[], id: string) { return this.actions.planner.mapHints(hints, id); }
  private getFallbackRefreshTargets(id: string, result: DecisionActionResult) { return this.actions.planner.getFallbackTargets(id, result); }
  private prunePatchedTargets(targets: ReadonlySet<HomeAutoRefreshTarget>, result: DecisionActionResult) { return this.actions.planner.prunePatchedTargets(targets, result); }
  private shouldPatchActionResultLocally(result: DecisionActionResult) { return this.actions.planner.shouldPatchLocally(result); }
  private canPatchDecisionSlicesLocally(result: DecisionActionResult) { return this.actions.planner.canPatchDecisionSlicesLocally(result); }
  private needsDatesRefreshFromResult(result: DecisionActionResult) { return this.actions.planner.needsDatesRefresh(result); }
  private shouldRefreshDetailForDecision(id: string) { return this.actions.planner.shouldRefreshDetail(id); }
  private rememberActionEchoContext(id: string, request: DecisionActionRequest, result: DecisionActionResult, targets: readonly HomeAutoRefreshTarget[]): void {
    this.actions.echo.remember(id, request, result, targets);
  }
  private clearActionFeedback(): void { this.actions.clearFeedback(); }
  private buildLocalError(): UxError { return this.actions.buildLocalError(); }
}
